/* ==========================================================================
   Hero-carrousel van een boetiek

   Wisselt de beelden en herstart per beeld de Ken Burns-animatie. Alleen dat.
   De overvloeier en de zoom zitten in CSS, want dat is waar ze thuishoren.

   ---------------------------------------------------------------------------
   Waarom niet volledig in CSS
   ---------------------------------------------------------------------------
   Kan wel, met per slide een animation-delay over een cyclus van n keer de
   slideduur. Maar de percentages in @keyframes kunnen geen CSS-variabele
   gebruiken, dus je hebt een aparte keyframe-set nodig per aantal beelden.
   Bij negen boetieks met elk een ander aantal foto's wordt dat onwerkbaar.

   ---------------------------------------------------------------------------
   Wat bewust zo is
   ---------------------------------------------------------------------------
   Zonder JavaScript blijft het eerste beeld staan. Geen lege hero, en ook
   geen pauzeknop: die staat verborgen tot dit script hem toont.

   Bij prefers-reduced-motion draait de carrousel niet. Wie beweging heeft
   uitgezet, krijgt één stilstaand beeld, en dan is er ook geen knop.

   Pijltjes om zelf te bladeren staan er wel bij reduced motion: zelf
   bladeren is geen beweging die vanzelf komt. Zonder JavaScript zijn ze
   verborgen, want dan doen ze niets.

   Voor alle anderen is er een pauzeknop. Een hero die vanzelf wisselt valt
   onder WCAG 2.2.2, en reduced motion alleen volstaat daar niet: lang niet
   iedereen die last heeft van beweging, weet dat die instelling bestaat.
   Daarnaast staat de carrousel stil zolang de muis erboven hangt of de focus
   erin staat, en buiten beeld.

   Twee soorten stilstaan, en dat verschil is geen detail:
   - stop: buiten beeld of reduced motion. De zoom gaat eraf en begint bij
     terugkomst opnieuw.
   - vasthouden: pauzeknop, muis of focus. De zoom bevriest waar hij is, en
     bij het verdergaan krijgt het beeld alleen de tijd die het nog had.
     Anders springt het beeld terug naar zijn beginstand zodra je er met de
     muis overheen gaat, en blijft het na het verdergaan te lang staan.
   ========================================================================== */

(() => {
  const traag = window.matchMedia('(prefers-reduced-motion: reduce)');

  // Na het vasthouden nooit meteen wisselen: dat voelt als een schok
  const MINIMUM_RESTTIJD = 1000;

  class Carrousel {
    constructor(el) {
      this.el = el;
      this.slides = Array.from(el.querySelectorAll('[data-bhc-slide]'));
      if (this.slides.length < 2) return;

      this.duur = (parseFloat(el.dataset.bhcDuur) || 6) * 1000;
      this.index = Math.max(
        0,
        this.slides.findIndex((s) => s.classList.contains('is-actief'))
      );

      this.aan = false; // draait, of staat vast maar met de zoom erop
      this.vast = false; // vastgehouden door knop, muis of focus
      this.inBeeld = false;
      this.gepauzeerd = false; // met de knop, door de bezoeker zelf
      this.zweeft = false;
      this.focusBinnen = false;
      this.resterend = null;

      // Eén signaal voor alle luisteraars op de elementen, zie opruimen
      this.luisteraars = new AbortController();
      this.koppelKnop(this.luisteraars.signal);
      this.koppelPijlen(this.luisteraars.signal);
      this.koppelVasthouden(this.luisteraars.signal);
      this.toonKnop();

      this.bewaak();
      this.opTraag = () => this.herbekijk();
      traag.addEventListener('change', this.opTraag);
    }

    /* De theme editor gooit de sectie weg bij elke wijziging. Zonder dit
       blijven de timer, de waarnemer en de luisteraars op de oude, al
       losgekoppelde elementen doorlopen. */
    opruimen() {
      this.stop();
      this.waarnemer?.disconnect();
      this.luisteraars?.abort();
      if (this.opTraag) traag.removeEventListener('change', this.opTraag);
    }

    /* Buiten beeld hoeft er niets te draaien. Scheelt werk voor de browser en
       voorkomt dat je halverwege een reeks binnenkomt. */
    bewaak() {
      this.waarnemer = new IntersectionObserver(
        (entries) => {
          // Het laatste bericht is de huidige stand
          this.inBeeld = entries[entries.length - 1].isIntersecting;
          this.herbekijk();
        },
        { threshold: 0.15 }
      );
      this.waarnemer.observe(this.el);
    }

    /* --- Pauzeknop en vasthouden ----------------------------------------- */

    koppelKnop(signal) {
      this.knop = this.el.querySelector('[data-bhc-knop]');
      if (!this.knop) return;

      this.knop.addEventListener(
        'click',
        () => {
          if (this.gepauzeerd) {
            this.gepauzeerd = false;
            /* Wie uitdrukkelijk op afspelen drukt, wil het zien draaien. Maar
               de muis en de focus staan op dat moment op de knop, dus binnen
               de carrousel, en die zouden hem meteen weer vasthouden. Een
               toetsenbordgebruiker zag dan nooit iets gebeuren. Dus tellen
               ze niet meer tot ze de carrousel verlaten en terugkomen. */
            this.zweeft = false;
            this.focusBinnen = false;
          } else {
            this.gepauzeerd = true;
          }
          this.werkKnopBij();
          this.herbekijk();
        },
        { signal }
      );
    }

    /* --- Pijltjes en toetsen ---------------------------------------------- */

    koppelPijlen(signal) {
      this.pijlen = Array.from(this.el.querySelectorAll('[data-bhc-pijl]'));
      this.live = this.el.querySelector('[data-bhc-live]');

      this.pijlen.forEach((pijl) => {
        pijl.hidden = false;
        pijl.addEventListener(
          'click',
          () => this.handmatig(this.index + Number(pijl.dataset.bhcPijl)),
          { signal }
        );
      });

      /* Pijltjestoetsen werken zolang de focus in de carrousel staat, dus
         op een knop. Daarbuiten blijven ze gewoon scrollen. */
      this.el.addEventListener(
        'keydown',
        (event) => {
          if (event.altKey || event.ctrlKey || event.metaKey) return;
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
          event.preventDefault();
          this.handmatig(this.index + (event.key === 'ArrowRight' ? 1 : -1));
        },
        { signal }
      );
    }

    /* Zelf bladeren. Het beeld begint met een volle beurt en een verse zoom.
       Draait de carrousel, dan loopt de timer opnieuw. Staat hij vast (knop,
       muis of focus, en na een klik op een pijl is dat meestal zo), dan
       krijgt het beeld een volle beurt zodra hij verdergaat. */
    handmatig(nummer) {
      this.wissel(nummer);

      if (this.aan) {
        if (this.vast) this.resterend = this.duur;
        else this.plan(this.duur);
      }

      this.meld();
    }

    // Laat een schermlezer horen welke foto er nu staat
    meld() {
      if (!this.live) return;
      const sjabloon = this.live.dataset.bhcPositie;
      if (!sjabloon) return;
      this.live.textContent = sjabloon
        .replace('[nummer]', String(this.index + 1))
        .replace('[totaal]', String(this.slides.length));
    }

    /* Het label wisselt mee met wat de knop zal doen. De teksten komen
       vertaald uit Liquid. */
    werkKnopBij() {
      if (!this.knop) return;
      const label = this.gepauzeerd
        ? this.knop.dataset.labelAfspelen
        : this.knop.dataset.labelPauzeren;
      if (label) this.knop.setAttribute('aria-label', label);
      this.knop.toggleAttribute('data-bhc-gepauzeerd', this.gepauzeerd);
    }

    // Bij reduced motion draait er niets, dus is er ook niets te pauzeren
    toonKnop() {
      if (this.knop) this.knop.hidden = traag.matches;
    }

    koppelVasthouden(signal) {
      /* Aanraken telt niet als zweven. Op een telefoon komen pointerenter en
         pointerleave bij elke tik vlak na elkaar, en dan zou de carrousel
         voor niets even stilvallen. */
      this.el.addEventListener(
        'pointerenter',
        (event) => {
          if (event.pointerType === 'touch') return;
          this.zweeft = true;
          this.herbekijk();
        },
        { signal }
      );
      this.el.addEventListener(
        'pointerleave',
        (event) => {
          if (event.pointerType === 'touch') return;
          this.zweeft = false;
          this.herbekijk();
        },
        { signal }
      );

      this.el.addEventListener(
        'focusin',
        () => {
          this.focusBinnen = true;
          this.herbekijk();
        },
        { signal }
      );
      this.el.addEventListener(
        'focusout',
        (event) => {
          if (this.el.contains(event.relatedTarget)) return;
          this.focusBinnen = false;
          this.herbekijk();
        },
        { signal }
      );
    }

    /* --- Draaien, vasthouden, stoppen ------------------------------------ */

    herbekijk() {
      this.toonKnop();

      if (traag.matches || !this.inBeeld) {
        this.stop();
        return;
      }

      if (this.gepauzeerd || this.zweeft || this.focusBinnen) this.houVast();
      else this.speel();
    }

    speel() {
      if (this.aan && !this.vast) return;

      // Na vasthouden de tijd die het beeld nog had, anders een volle beurt
      const wacht =
        this.vast && this.resterend !== null
          ? Math.max(MINIMUM_RESTTIJD, this.resterend)
          : this.duur;

      this.aan = true;
      this.vast = false;
      this.resterend = null;
      this.el.setAttribute('data-bhc-draait', 'true');
      this.el.removeAttribute('data-bhc-vast');
      this.plan(wacht);
    }

    houVast() {
      /* Nog niet begonnen, bijvoorbeeld in beeld gekomen terwijl de knop op
         pauze stond: dan staat er geen zoom op en valt er niets te bevriezen.
         Afspelen begint straks gewoon met een volle beurt. */
      if (!this.aan || this.vast) return;

      this.vast = true;
      this.resterend = Math.max(0, this.volgendeOp - performance.now());
      clearTimeout(this.timer);
      this.el.setAttribute('data-bhc-vast', '');
    }

    stop() {
      this.aan = false;
      this.vast = false;
      this.resterend = null;
      clearTimeout(this.timer);
      this.el.removeAttribute('data-bhc-draait');
      this.el.removeAttribute('data-bhc-vast');
    }

    /* setTimeout en geen setInterval: na het vasthouden moet de eerstvolgende
       wissel korter zijn dan een volle beurt. */
    plan(wacht) {
      clearTimeout(this.timer);
      this.volgendeOp = performance.now() + wacht;
      this.timer = setTimeout(() => {
        this.volgende();
        this.plan(this.duur);
      }, wacht);
    }

    volgende() {
      this.wissel(this.index + 1);
    }

    // Wisselt van beeld, rond aan beide kanten (na de laatste komt de eerste)
    wissel(nummer) {
      const aantal = this.slides.length;
      const doel = ((nummer % aantal) + aantal) % aantal;
      if (doel === this.index) return;

      const vorige = this.slides[this.index];
      this.index = doel;
      const nu = this.slides[this.index];

      vorige.classList.remove('is-actief');
      nu.classList.add('is-actief');

      this.herstartZoom(nu);
    }

    /* De zoom moet bij elk beeld weer van voren beginnen. Een animatie
       herstart niet door de klasse opnieuw te zetten; de browser ziet geen
       wijziging. Even weghalen, een frame wachten en terugzetten wel. */
    herstartZoom(slide) {
      const img = slide.querySelector('.bhc__img');
      if (!img) return;
      img.style.animation = 'none';
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          img.style.animation = '';
        });
      });
    }
  }

  function start() {
    document.querySelectorAll('[data-bhc]').forEach((el) => {
      if (el.dataset.bhcKlaar) return;
      el.dataset.bhcKlaar = 'true';
      el.bhcCarrousel = new Carrousel(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  // Theme editor: een sectie hertekenen levert nieuwe elementen op
  document.addEventListener('shopify:section:load', start);
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-bhc]').forEach((el) => el.bhcCarrousel?.opruimen());
  });
})();
